import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableCompareTrigger } from "../../../components/lcaComparison";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addFlow43_4LCA,
  deleteFlow43_4LCA,
  getAllFlow43_4LCA,
  updateFlow43_4LCA,
} from "../../../../../../apis/Product/TransportationEoLFlow43Api";
import {
  getProductFlow43_4DisposalMethodDropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../../apis/Dropdown";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const Flow43_4Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [disposalMethodDropdown, setDisposalMethodDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [unitDisposal, setUnitDisposal] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductFlow43_4DisposalMethodDropdown({
      setDropdown: setDisposalMethodDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllFlow43_4LCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && disposalMethodDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const disposalCode =
          typeof row.current_disposal_method === "string"
            ? row.current_disposal_method
            : row.current_disposal_method?.value || row.current_disposal_method?.code;
        if (disposalCode) {
          const selected = disposalMethodDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === disposalCode || opt.value === disposalCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitDisposal(newUnits);
    }
  }, [data, disposalMethodDropdown]);

  // Convert country codes to names when dropdowns are loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          const updates = {};
          
          // Convert treatment_country code to name
          if (item.treatment_country && typeof item.treatment_country === "string") {
            if (item.treatment_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.treatment_country
              );
              if (foundCountry && item.treatment_country !== foundCountry.name) {
                hasChanges = true;
                updates.treatment_country = foundCountry.name;
              }
            }
          }
          
          return hasChanges ? { ...item, ...updates } : item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      current_disposal_method: {
        targetField: "quantity_per_fu",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitDisposal((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "current_disposal_method",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const disposalCode =
        typeof editedItem.current_disposal_method === "string"
          ? editedItem.current_disposal_method
          : editedItem.current_disposal_method?.value || 
            editedItem.current_disposal_method?.code || 
            null;

      // Convert country name back to code for backend
      let treatmentCountryCode = editedItem.treatment_country;
      if (treatmentCountryCode) {
        if (typeof treatmentCountryCode === "object") {
          treatmentCountryCode = treatmentCountryCode.code || treatmentCountryCode.value || treatmentCountryCode.name || null;
        } else if (typeof treatmentCountryCode === "string" && treatmentCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === treatmentCountryCode || option.value === treatmentCountryCode
          );
          if (foundCountry) {
            treatmentCountryCode = foundCountry.code;
          }
        }
      }

      updateFlow43_4LCA({
        productId,
        rowId: editedItem.id,
        surplus_resource: editedItem.surplus_resource || null,
        current_disposal_method: disposalCode,
        quantity_per_fu: editedItem.quantity_per_fu || 0,
        treatment_country: treatmentCountryCode || null,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () => {
          window.dispatchEvent(new Event('refresh-transportation-eol-43-totals'));
        },
      });
    },
    [productId, data, countryDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const disposalCode =
        typeof newItem.current_disposal_method === "string"
          ? newItem.current_disposal_method
          : newItem.current_disposal_method?.value || 
            newItem.current_disposal_method?.code || 
            null;

      // Convert country name back to code for backend
      let treatmentCountryCode = newItem.treatment_country;
      if (treatmentCountryCode) {
        if (typeof treatmentCountryCode === "object") {
          treatmentCountryCode = treatmentCountryCode.code || treatmentCountryCode.value || treatmentCountryCode.name || null;
        } else if (typeof treatmentCountryCode === "string" && treatmentCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === treatmentCountryCode || option.value === treatmentCountryCode
          );
          if (foundCountry) {
            treatmentCountryCode = foundCountry.code;
          }
        }
      }

      addFlow43_4LCA({
        productId,
        surplus_resource: newItem.surplus_resource || null,
        current_disposal_method: disposalCode,
        quantity_per_fu: newItem.quantity_per_fu || 0,
        treatment_country: treatmentCountryCode || null,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-transportation-eol-43-totals'));
        },
        use_swal: true,
      });
    },
    [productId, fetchData, countryDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteFlow43_4LCA({
        productId,
        rowId: itemToDelete.id,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-transportation-eol-43-totals'));
        },
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "surplus_resource",
        accessor: "surplus_resource",
        header: t("SURPLUS_RESOURCE") || "Surplus Resource",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "current_disposal_method",
        accessor: "current_disposal_method",
        header: `${t("CURRENT_DISPOSAL_METHOD") || "Current disposal method"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_DISPOSAL_METHOD") || "Select disposal method...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "current_disposal_method",
            disposalMethodDropdown,
            unitDisposal,
            rowId
          ),
      },
      {
        id: "quantity_per_fu",
        accessor: "quantity_per_fu",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "current_disposal_method",
            disposalMethodDropdown,
            unitDisposal,
            rowId
          ),
      },
      {
        id: "treatment_country",
        accessor: "treatment_country",
        header: `${t("TREATMENT_COUNTRY") || "Treatment country"} (Dropdown)`,
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitDisposal,
      disposalMethodDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      current_disposal_method: disposalMethodDropdown || [],
      treatment_country: countryDropdown || [],
    }),
    [disposalMethodDropdown, countryDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
        }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default Flow43_4Table;

